import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { LocalizedNameMap } from 'pi-kiosk-shared';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { shouldRefetchCustomerOnReconnect } from '../../shared/providers/customerQueryClient.js';
import {
  orgDirectoryListRefetchInterval,
  orgDirectoryStaleTime,
} from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';

export interface PickupPointOption {
  id: number;
  code: string;
  name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  nameLocales?: LocalizedNameMap | null;
  isOnSpot: boolean;
  /**
   * P0 Mode S — server `isOpenNow` when ListPickupPoints exposes it.
   * Prefer over client evaluation of {@link openHoursJson}.
   */
  isOpenNow?: boolean | null;
  /**
   * P0 Mode S — on-spot weekly hours from ListPickupPointsForKiosk DTO.
   */
  openHoursJson?: Record<string, unknown> | null;
}

/**
 * G1/G7 — Product-safe on-spot option when pickup-points GET fails or returns [].
 * Prefer real ensure-created point ids from a successful query. Synthetic id equals
 * salesPointId; CollectMetadataPolicy / cash prepare remap that alias to the
 * confirmed on-spot pickup point id on write.
 */
export function synthesizeOnSpotPickupOption(
  salesPointId: number,
  salesPointName?: string | null,
): PickupPointOption {
  const trimmed = salesPointName?.trim();
  return {
    id: salesPointId,
    code: `on-spot-sp-${salesPointId}`,
    name: trimmed !== undefined && trimmed.length > 0 ? trimmed : `Sales point ${salesPointId}`,
    isOnSpot: true,
  };
}

export type ResolveCollectPickupPointsInput = {
  readonly salesPointId: number | null | undefined;
  readonly queryPoints: readonly PickupPointOption[] | undefined;
  readonly isError: boolean;
  /** First-fetch pending — do not synthesize yet (prefer ensure-created ids). */
  readonly isLoading?: boolean;
  readonly fallbackName?: string | null;
};

export type ResolveCollectPickupPointsResult = {
  readonly points: readonly PickupPointOption[];
  readonly usedFallback: boolean;
  /**
   * True only when SP is unknown and the query failed with no usable points.
   * Never true when a positive salesPointId allows on-spot synthesis (G1).
   */
  readonly pointsLoadError: boolean;
};

/**
 * G1/G7 Absolute DoD — for known positive salesPointId/kioskId:
 * 1. Prefer query data when length > 0
 * 2. On isError or empty success → synthetic on-spot; never strand loadError/noPoints
 */
export function resolveCollectPickupPoints(
  input: ResolveCollectPickupPointsInput,
): ResolveCollectPickupPointsResult {
  const salesPointId = input.salesPointId;
  const knownSp =
    salesPointId !== null && salesPointId !== undefined && salesPointId > 0;
  const queryPoints = input.queryPoints;

  if (queryPoints !== undefined && queryPoints.length > 0) {
    return {
      points: queryPoints,
      usedFallback: false,
      pointsLoadError: false,
    };
  }

  if (input.isLoading === true) {
    return {
      points: [],
      usedFallback: false,
      pointsLoadError: false,
    };
  }

  if (knownSp) {
    return {
      points: [synthesizeOnSpotPickupOption(salesPointId, input.fallbackName)],
      usedFallback: true,
      pointsLoadError: false,
    };
  }

  return {
    points: [],
    usedFallback: false,
    pointsLoadError: input.isError,
  };
}

/** G12 — directory SSE stale/poll + tab-return parity (checkout pickup lists). */
export type KioskPickupPointsRealtimeOptions = {
  readonly directorySseConnected: boolean;
};

export function kioskPickupPointsQueryOptions(
  sseConnected: boolean,
): {
  staleTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: true;
  refetchOnReconnect: typeof shouldRefetchCustomerOnReconnect;
} {
  return {
    staleTime: orgDirectoryStaleTime(sseConnected),
    refetchInterval: orgDirectoryListRefetchInterval(sseConnected),
    refetchOnWindowFocus: true,
    refetchOnReconnect: shouldRefetchCustomerOnReconnect,
  };
}

/**
 * Core checkout pickup list — always fetched when a sales point is known.
 * Backend gates this on `surface_customer` only (Spec carve-out — not denyable via infra).
 * Backend always ensures the sales point itself as on-spot pickup when no active
 * links exist (dedicated `pickup_points` entitlement is optional / mirror-safe).
 * Optional surfaces (find-order, account pickup QR, scheduled LATER) require
 * `order_pickup_infrastructure` (+ `scheduled_pickup` for slots); see PICKUP_ENTITLEMENT.md.
 */
export function useKioskPickupPoints(
  kioskId: number | null | undefined,
  realtime?: KioskPickupPointsRealtimeOptions,
): UseQueryResult<PickupPointOption[], Error> {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.kioskPickupPoints(tenantCode, kioskId ?? 0),
    queryFn: async () => {
      const data = await api.request<{ points: PickupPointOption[] }>(
        `/customer-checkout/sales-points/${encodeURIComponent(String(kioskId))}/pickup-points`
      );
      return data.points;
    },
    enabled: kioskId !== null && kioskId !== undefined && kioskId > 0,
    // Transient ensure races / 5xx must not strand collect on loadError.
    retry: 2,
    ...(realtime !== undefined
      ? kioskPickupPointsQueryOptions(realtime.directorySseConnected)
      : { staleTime: 60_000, refetchOnReconnect: shouldRefetchCustomerOnReconnect }),
  });
}
